import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  ArrowLeft, User, Mail, Phone, Calendar, MapPin, FileText,
  Shield, Users, Briefcase, CreditCard, Clock, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';

export default function UserProfileDetailed() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setUserId(parseInt(user.id, 10));
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);


  useEffect(() => {
    if (!userId) return;
    api.get(`/utilisateurs/${userId}`)
      .then(res => {
        let data = res.data.utilisateur ||
                   (Array.isArray(res.data.utilisateurs) ? res.data.utilisateurs[0] : null) ||
                   res.data;
        if (data) setUser(data);
        else setError('Aucun utilisateur trouvé.');
      })
      .catch(() => {
        setError('Erreur lors de la récupération du profil');
        toast.error('Impossible de charger le profil');
      })
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-terrecuite"></div>
    </div>
  );

  if (error) return (
    <div className="max-w-lg mx-auto mt-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
      <div className="flex items-center">
        <Trash2 className="h-5 w-5 text-red-500" />
        <p className="ml-3 text-sm text-red-700">{error}</p>
      </div>
    </div>
  );

  const infoSections = [
    {
      title: 'Informations personnelles',
      icon: <User className="h-5 w-5 text-terrecuite mr-2" />, 
      fields: [
        ['CIN', user.cin],
        ['Date de naissance', user.date_naissance],
        ['Sexe', user.sexe],
        ['Email', user.email],
        ['Téléphone', user.telephone],
        ['Adresse', user.adresse]
      ]
    },
    {
      title: 'Informations professionnelles',
      icon: <Briefcase className="h-5 w-5 text-terrecuite mr-2" />, 
      fields: [
        ['Login', user.login],
        ['Date d\'embauche', user.date_embauche],
        ['Type de contrat', user.type_contrat],
        ['RIB', user.rib],
        ['Rôle', user.role?.nom],
        ['Équipe', user.module?.nom]
      ]
    },
    {
      title: 'Métadonnées système',
      icon: <Shield className="h-5 w-5 text-terrecuite mr-2" />, 
      fields: [
        ['Créé le', user.created_at],
        ['Mis à jour le', user.updated_at],
        ['Supprimé le', user.deleted_at]
      ]
    }
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6 flex items-center">
          <button onClick={() => navigate(-1)} className="flex items-center text-terrecuite hover:text-terrecuite/80 mr-4">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">Profil Utilisateur</h1>
        </div>
      </div>

      {/* Profile Header */}
      <div className="container mx-auto px-4 py-8 flex flex-col md:flex-row items-center gap-6">
  {user.photo ? (
    <img
      src={user.photo.startsWith('http') ? user.photo : `http://127.0.0.1:8000/storage/${user.photo}`}
      alt={`${user.nom} ${user.prenom}`}
      className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-full border-4 border-white shadow-lg"
    />
  ) : (
    <div className="flex items-center justify-center w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg bg-gray-200">
      <User className="w-12 h-12 text-terrecuite" />
    </div>
  )}
        <div>
          <h2 className="text-3xl font-bold text-gray-800">{user.prenom} {user.nom}</h2>
          <p className="text-gray-600 mt-1">ID: {user.id}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-terrecuite/10 text-terrecuite">{user.role?.nom}</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{user.module?.nom}</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{user.type_contrat}</span>
          </div>
        </div>
      </div>

      {/* Info Sections */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {infoSections.map(({ title, icon, fields }) => (
            <div key={title} className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 flex items-center">
                {icon}{title}
              </h3>
              <ul className="space-y-4">
                {fields.map(([label, val]) => (
                  <li key={label} className="flex items-start">
                    <div className="flex-shrink-0 mt-0.5">
                      {/** reuse icons based on label if desired **/}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">{label}</p>
                      <p className="text-sm font-medium text-gray-800">{
                        label.includes('Date') || label.includes('Créé') ? new Date(val).toLocaleDateString() : (val || 'N/A')
                      }</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
